import { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import Button from '@rio-cloud/rio-uikit/Button';
import ButtonToolbar from '@rio-cloud/rio-uikit/ButtonToolbar';
import Fade from '@rio-cloud/rio-uikit/Fade';
import isEmpty from 'lodash/fp/isEmpty';
import classNames from 'classnames';

import { useNotifications } from './useNotifications';
import { NotificationItemNew } from './NotificationItemNew';
import { StaggeredList } from '../../components/StaggeredList';
import { useGroupedNotifications } from './useGroupedNotifications';
import { EmptyVehicleNotificationList } from './EmptyVehicleNotificationList';
import { NoNotificationsAvailable } from './NoNotificationsAvailable';
import { DateDivider } from './DateDivider';
import { NotificationsToolbar } from './NotificationsToolbar';
import { Loader } from './Loader';

type NotificationsProps = {
    isVehicleMode: boolean;
};

export const NotificationsNew = ({ isVehicleMode }: NotificationsProps) => {
    const [showReadMessages, setShowReadMessages] = useState(false);
    const [showLoadMoreHint, setShowLoadMoreHint] = useState(false);

    const {
        notifications,
        isLoading,
        newNotificationsCount,
        hasNext: hasMoreNotifications,
        markAllAsRead,
        fetchMore,
        isFetchMoreLoading,
        isFetchMoreSuccess,
        updateSingleNotification,
    } = useNotifications(isVehicleMode);

    const toggleReadMessages = () => setShowReadMessages(value => !value);

    const handleLoadMore = () => {
        fetchMore();
    };

    useEffect(() => {
        if (isFetchMoreSuccess) {
            setShowLoadMoreHint(true);
        }
    }, [isFetchMoreSuccess]);

    const groupedNotifications = useGroupedNotifications(notifications ?? [], showReadMessages);

    if (isLoading && notifications === undefined) {
        return <Loader />;
    }

    // Initially, the notification list is undefined before the first fetch request.
    // In this state, don't render the empty state screen as it would flash for a split-second.
    if (notifications !== undefined && isEmpty(notifications)) {
        return isVehicleMode ? <EmptyVehicleNotificationList /> : <NoNotificationsAvailable />;
    }

    const notificationListClassName = classNames(
        'notifications-list',
        'padding-bottom-15',
        !isVehicleMode && 'padding-top-0 padding-x-15'
    );

    const hasNewNotifications = newNotificationsCount > 0;

    // Show the load more button only when read messages are shown and there are more notifications on server side
    // or when there are new unread notifications and there are more notifications on server side.
    // Avoid showing the load more button when there are no new notifications
    const showLoadMoreButton =
        (showReadMessages && hasMoreNotifications) || (hasNewNotifications && hasMoreNotifications);

    return (
        <div className='position-relative'>
            <NotificationsToolbar
                showReadMessages={showReadMessages}
                hasUnreadMessages={hasNewNotifications}
                onToggleReadMessages={toggleReadMessages}
                onMarkAllAsRead={markAllAsRead}
                isVehicleMode={isVehicleMode}
            />

            {/** biome-ignore lint/a11y/useAriaPropsSupportedByRole: might be used in test */}
            <div className={notificationListClassName} data-testid='notifications-list' aria-label='notifications-list'>
                {!groupedNotifications.today && (
                    <div
                        key='today'
                        className='no-today-notifications text-center text-color-gray text-italic padding-y-20'
                    >
                        <FormattedMessage
                            id='intl-msg:notifications.noNewToday'
                            defaultMessage='No new notifications today'
                        />
                    </div>
                )}

                {groupedNotifications.today && (
                    <div key='today'>
                        <StaggeredList
                            className='display-flex flex-column gap-10'
                            items={groupedNotifications.today}
                            keyItem='id'
                            renderItem={notification => (
                                <NotificationItemNew
                                    notification={notification}
                                    isVehicleMode={isVehicleMode}
                                    onUpdateNotification={updateSingleNotification}
                                />
                            )}
                        />
                    </div>
                )}

                {groupedNotifications.yesterday && (
                    <div key='yesterday'>
                        <DateDivider>
                            <FormattedMessage
                                id='intl-msg:notifications.dateDivider.yesterday'
                                defaultMessage='Yesterday'
                            />
                        </DateDivider>
                        <StaggeredList
                            className='display-flex flex-column gap-10'
                            items={groupedNotifications.yesterday}
                            keyItem='id'
                            renderItem={notification => (
                                <NotificationItemNew
                                    notification={notification}
                                    isVehicleMode={isVehicleMode}
                                    onUpdateNotification={updateSingleNotification}
                                />
                            )}
                        />
                    </div>
                )}

                {groupedNotifications.older && (
                    <div key='older'>
                        <DateDivider>
                            <FormattedMessage id='intl-msg:notifications.dateDivider.older' defaultMessage='Older' />
                        </DateDivider>
                        <StaggeredList
                            className='display-flex flex-column gap-10'
                            items={groupedNotifications.older}
                            keyItem='id'
                            renderItem={notification => (
                                <NotificationItemNew
                                    notification={notification}
                                    isVehicleMode={isVehicleMode}
                                    onUpdateNotification={updateSingleNotification}
                                />
                            )}
                        />
                    </div>
                )}
            </div>

            {showLoadMoreButton && (
                <>
                    <ButtonToolbar className='justify-content-center margin-bottom-15'>
                        <Button
                            bsStyle='default'
                            variant='link'
                            data-testid='load-more-btn'
                            onClick={handleLoadMore}
                            iconName={isFetchMoreLoading ? '' : 'rioglyph-arrow-down'}
                            className={isFetchMoreLoading ? 'btn-loading' : ''}
                        >
                            <FormattedMessage id='intl-msg:notifications.loadMore' />
                        </Button>
                    </ButtonToolbar>
                    {/* show only after first "load more" click */}
                    <Fade show={!showReadMessages && showLoadMoreHint}>
                        <p className='text-color-gray text-size-11 text-italic text-center text-wrap-balance padding-x-50 margin-top--10'>
                            <FormattedMessage
                                id='intl-msg:notifications.loadMoreHint'
                                defaultMessage='Some loaded messages may be hidden. Click “Show read” in the Display options to view all.'
                            />
                        </p>
                    </Fade>
                </>
            )}
        </div>
    );
};
